import api from '../services/api';
import saveFile from './saveFile';

/**
 * Enterprise Report PDF Generator
 * Generates PDF reports for all pages by calling the backend API
 * Supports: dashboard, clients, calendar, surveys, teachers, groups, billing
 */
class ReportPdfGenerator {
  /**
   * Generate a PDF report
   * @param {string} reportType - 'dashboard' | 'clients' | 'calendar' | 'surveys' | 'teachers' | 'groups' | 'billing'
   * @param {Object} data - Report data specific to the report type
   * @param {string} language - 'en' | 'ar' | 'he'
   * @param {string} centerType - 'therapy' | 'learning'
   * @returns {Promise<{success: boolean, blob: Blob, filename: string}>}
   */
  async generateReport(reportType, data, language = 'en', centerType = 'learning') {
    try {
      const response = await api.post(
        '/pdf/report',
        { reportType, data, language, centerType },
        {
          responseType: 'blob',
          headers: { 'Accept': 'application/pdf' },
          timeout: 120000
        }
      );

      // Validate response
      if (!response.data) {
        throw new Error('No data received from server');
      }

      const contentType = response.headers?.['content-type'] || '';
      if (!contentType.includes('application/pdf') && !contentType.includes('octet-stream')) {
        if (contentType.includes('application/json')) {
          try {
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            throw new Error(errorData.message || errorData.error || 'Server returned an error');
          } catch (parseError) {
            if (parseError.message.includes('Server returned')) throw parseError;
            throw new Error('Server returned unexpected response');
          }
        }
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (blob.size === 0) {
        throw new Error('Received empty PDF');
      }

      // Check for small responses that might be errors
      if (blob.size < 1000) {
        try {
          const text = await blob.text();
          const jsonData = JSON.parse(text);
          if (jsonData.error || jsonData.message) {
            throw new Error(jsonData.error || jsonData.message);
          }
        } catch (e) {
          if (e.message.includes('error') || e.message.includes('Failed')) throw e;
        }
      }

      const date = new Date().toISOString().split('T')[0];
      const filename = `${reportType}_report_${date}.pdf`;

      return { success: true, blob, filename };
    } catch (err) {
      let errorMessage = 'Failed to generate report';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) { /* ignore */ }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Generate and download a PDF report
   */
  async downloadReport(reportType, data, language = 'en', centerType = 'learning') {
    const result = await this.generateReport(reportType, data, language, centerType);
    // saveFile: anchor download on web (unchanged), Filesystem+share on native —
    // a Capacitor WebView cannot trigger a browser download.
    await saveFile(result.blob, result.filename, 'application/pdf');
    return result;
  }
}

export default ReportPdfGenerator;
